import { useId } from "react";
import { formatEther } from "viem";
import type { MintFeeView, NetBuyView, ProofEligibilityView, RewardPolicyView } from "../../../state/ui-types";
import { ArrowUpRightIcon, InfoIcon, LeafIcon, ShieldCheckIcon } from "../../../components/Icons";
import { explainBlockingReason, formatUsdExact } from "../../../components/format";
import { UsdAmount } from "../../../components/UsdAmount";
import { Overlay } from "../../../components/overlays/Overlay";
import { formatRewardRate } from "../../../lib/reward-format";
import { TierDisplayNotice } from "../../../components/TierDisplayNotice";
import { TierPortrait, tierName } from "../../../components/TierPortrait";

interface TierDialogProps {
  open: boolean;
  netBuy: NetBuyView;
  fee: MintFeeView;
  policy: RewardPolicyView;
  demoMode: boolean;
  proofEligibility?: ProofEligibilityView | undefined;
  onRefresh?(): void;
  errorMessage?: string | null;
  errorCode?: string | null;
  onClose(): void;
  onSelect(tierUsd: number): void;
}

export function TierSelectionDialog({ open, netBuy, fee, policy, demoMode, proofEligibility, onRefresh, errorMessage, errorCode, onClose, onSelect }: TierDialogProps) {
  const explanationId = useId();
  const display = demoMode ? netBuy.tierDisplay : proofEligibility?.display;
  const tiers = demoMode ? (netBuy.tierDisplay.status === "UNAVAILABLE" ? null : netBuy.tierDisplay.eligibleTiers) :
    proofEligibility?.displayTiers !== undefined ? proofEligibility.displayTiers : proofEligibility?.decision.usableTiers ?? null;
  return (
    <Overlay open={open} onClose={onClose} title="Choose an eligible tier" eyebrow="PROOF VERIFIED">
      <p id={explanationId} className="tier-explanation">Tier amounts are eligibility thresholds, not NFT purchase prices. The mint fee is separate.</p>
      <div className="verified-banner"><ShieldCheckIcon /><div><strong>Your proof is verified</strong><span>Choose a displayed tier to review. Mint eligibility is checked again before preparation. Net buy is not consumed.</span></div></div>
      {display ? <TierDisplayNotice display={display} /> : null}
      {errorMessage ? <><p role="status">{errorMessage}</p>{errorCode ? <details><summary>Error details</summary><p>Error code: <code>{errorCode}</code></p></details> : null}</> : null}
      {!demoMode && tiers !== null && tiers.length > 0 && proofEligibility?.decision.state !== "ALLOWED" ?
        <p role="status">Previously checked tiers remain available to browse. Mint preparation is disabled until current checks pass.
          {" "}{proofEligibility?.error ?? proofEligibility?.decision.reasons.join(", ")}</p> : null}
      <div className="tier-context">
        <div><span>{netBuy.tierDisplay.status === "LAST_CONFIRMED_SNAPSHOT" ? "Confirmed net buy" : "Current net buy"}</span><strong>{netBuy.netBuyEth ?? "—"} {fee.nativeSymbol ?? netBuy.nativeSymbol ?? "ETH"}</strong>{netBuy.netBuyUsd === null ? <small>USD unavailable</small> : <UsdAmount value={netBuy.netBuyUsd} />}<small>{netBuy.asOfBlock === null ? "Net-buy provenance unavailable" : `Net-buy block #${netBuy.asOfBlock}`}</small></div>
        <div><span>Separate mint fee</span><strong>{fee.tierMintFeesWei ? "Varies by tier" : `${fee.currentFeeEth ?? "—"} ${fee.nativeSymbol ?? netBuy.nativeSymbol ?? "ETH"}`}</strong><small>Rechecked at transaction execution</small></div>
      </div>
      {tiers === null ? (
        <div className="modal-blocked"><InfoIcon /><div><strong>{(demoMode ? netBuy.loading : proofEligibility?.loading) ? "Loading eligibility…" : "Tier selection is unavailable"}</strong><p>{demoMode ? netBuy.blockingReasons.map(explainBlockingReason).join(" ") : proofEligibility?.error ?? proofEligibility?.decision.message ?? "Refresh proof eligibility and try again."}</p><p>{proofEligibility?.decision.reasons.join(", ")}</p></div></div>
      ) : tiers.length === 0 ? (
        <div className="modal-blocked"><LeafIcon /><div><strong>No tier is currently available</strong><p>The displayed net buy does not meet any tier threshold.</p></div></div>
      ) : (
        <div className="tier-grid">
          {tiers.map((tier) => (
            <button type="button" className={`tier-choice tier-choice-${tier}`} key={tier} aria-describedby={explanationId} onClick={() => onSelect(tier)}>
              <TierPortrait tier={tier} className="tier-choice-ranger" />
              <span className="tier-name">{tierName(tier)}</span>
              <span className="tier-range">Reward range {policyRange(policy, tier) ?? "policy unavailable"}</span>
              <span className="tier-not-price">Eligibility tier: ${tier.toLocaleString("en-US")}</span>
              {fee.tierMintFeesWei ? <span className="tier-not-price">Mint fee: {fee.tierMintFeesWei[String(tier)] === undefined ? "—" :
                formatEther(BigInt(fee.tierMintFeesWei[String(tier)]!))} {fee.nativeSymbol ?? "HYPE"}</span> : null}
              {netBuy.netBuyThresholds?.find((threshold) => threshold.tierUsd === tier) ?
                <span className="tier-not-price">Minimum net buy: {formatUsdExact(netBuy.netBuyThresholds.find((threshold) => threshold.tierUsd === tier)!.minNetBuyUsd)}</span> : null}
              <ArrowUpRightIcon />
            </button>
          ))}
        </div>
      )}
      {tiers?.length === 0 && netBuy.netBuyThresholds?.length ? <p className="modal-footnote">
        {netBuy.netBuyThresholds.map((threshold) => `Tier ${threshold.tierUsd}: minimum net buy ${formatUsdExact(threshold.minNetBuyUsd)}`).join(" · ")}
      </p> : null}
      <div style={{ display: "flex", justifyContent: "center", marginTop: "16px" }}>
        {!demoMode && onRefresh ? <button type="button" className="secondary-action" onClick={onRefresh}>Refresh proof eligibility</button> : null}
      </div>
      <p className="modal-footnote">PoW does not choose the reward or lock a tier. The selected tier is checked again before preparation. {demoMode ? "This demo cannot send a transaction." : ""}</p>
    </Overlay>
  );
}

function policyRange(policy: RewardPolicyView, tierUsd: number): string | null {
  if (!policy.available || policy.tiers === null) return null;
  const tier = policy.tiers.find((candidate) => candidate.tierUsd === tierUsd);
  return tier ? `${formatRewardRate(tier.minRate, tier.rewardDenominator)}–${formatRewardRate(tier.maxRate, tier.rewardDenominator)}` : null;
}
